import { create } from 'zustand';
import { AuthUtils } from './Auth.utils';
import { WebViewAuthService } from './WebView.auth';
import { AuthJwtObject } from './Auth.types';

/**
 * Authentication store for managing auth state
 */
const useAuthStore = create<{
  appStarted: boolean;
  isLoggedIn: boolean;
  isWebView: boolean;
  isWebViewReady: boolean;
  sessionEpoch: number;
  adoptedTokenEpoch: number;
  setAppStarted: (appStarted: boolean) => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  beginSession: () => void;
  handleWebViewToken: (authObj: AuthJwtObject) => void;
  setWebViewReady: (ready: boolean) => void;
}>((set, get) => ({
  // Initial state
  appStarted: AuthUtils.isExpired(),
  isLoggedIn: !AuthUtils.isExpired(),
  isWebView: WebViewAuthService.isWebViewAuthEnabled(),
  isWebViewReady: false,

  /**
   * Monotonic counter of sessions ESTABLISHED in this document.
   *
   * Bumped by `startSession()` only when it began from a logged-out state and is past the
   * awaited profile fetch — i.e. a real authenticated round-trip succeeded and it was not
   * merely a token refresh under a session we already had. Consumers use it as the "a new
   * session began" edge; see shouldResetRecoveryBudget for why the simpler edges (isLoggedIn
   * false→true, logout, any startSession) are all reachable from the reload loop that the
   * recovery budget exists to bound.
   */
  sessionEpoch: 0,

  /**
   * Monotonic counter of native tokens ADOPTED in this document.
   *
   * Distinct from sessionEpoch, and deliberately so: adopting a token is not evidence of a
   * working session (that is what sessionEpoch means) and must never hand the loop budgets
   * back. This counter exists for the opposite reason — it is the only signal that a usable
   * token arrived without a document load to consume it.
   *
   * That case is reachable: `WebViewAuthService.navigations` is exhausted, or the reply landed
   * while offline, so `planAuthReplyNavigation` suppresses the `location.replace` that would
   * otherwise re-issue every query on the new token. The token is written to storage, but
   * nothing refetches — `attemptRecovery` is false on the terminal screen, `isWebviewAuthSettled`
   * needs brandsLoaded, and `Http.requestTokenFromMobile` only polls for 5s while a 401 is in
   * flight. So a late reply left the user being told to close and reopen the app while a valid
   * session sat in localStorage. AuthLayout watches this and refetches instead.
   */
  adoptedTokenEpoch: 0,

  // State setters
  setAppStarted: (appStarted: boolean) => set({ appStarted }),
  setIsLoggedIn: (isLoggedIn: boolean) => set({ isLoggedIn }),

  // Announce that a session was successfully established. Deliberately separate from
  // setIsLoggedIn: that flag is also flipped by adopting a native token, which is not
  // evidence of a working session and must not hand the loop budgets back.
  beginSession: () => set({ sessionEpoch: get().sessionEpoch + 1 }),

  // Handle authentication token from WebView
  // Receives the ALREADY-DECODED object: WebView.auth decodes once and decides whether the
  // token is an upgrade worth adopting, so by the time we are called the decision is made.
  handleWebViewToken: (authObj: AuthJwtObject) => {
    AuthUtils.setJwt(authObj);
    set({
      isLoggedIn: !AuthUtils.isExpired(authObj),
      adoptedTokenEpoch: get().adoptedTokenEpoch + 1,
    });
  },

  // Set WebView ready state
  setWebViewReady: (ready: boolean) => set({ isWebViewReady: ready }),
}));

export { useAuthStore };
